import type { Customer } from '~/types/adamTypes';

import { getServices } from '~/db/services';

export type UseCategoryCustomer = {
  customerCategory: ComputedRef<CategorySchema>;
  hasCustomerBanner: ComputedRef<boolean>;
  hasCustomerHeader: ComputedRef<boolean>;
  customerSponsorId: ComputedRef<string>;
  customerWebLink: ComputedRef<string>;
  customerColor: ComputedRef<string>;
  customerMedia: ComputedRef<string>;
  customerBaseline: ComputedRef<string>;
  customerSponsorInfo: ComputedRef<string>;
  customerSponsorInfoLink: Ref<string>;
  load: () => Promise<void>;
};

export const useCategoryAncestorsCustomer = (
  ancestors: Ref<CategorySchema[]>
): UseCategoryCustomer => {
  const { $db } = useNuxtApp();

  const { sponsorInfosService } = getServices($db);

  const customerSponsorInfoLink = ref<string>();

  const customerCategory = computed<CategorySchema>(() => {
    return toValue(ancestors).find((cat) => {
      return cat.customerSponsorId && cat.customerMedia && cat.customerBaseline;
    });
  });

  const hasCustomerBanner = computed<boolean>(() => {
    if (!customerCategory.value) return false;
    return customerBaseline.value != null && customerMedia.value != null;
  });

  const hasCustomerHeader = computed<boolean>(() => {
    if (!customerCategory.value) return false;
    return (
      toValue(customerBaseline) != null &&
      toValue(customerCategory)?.media != null
    );
  });

  const customerSponsorId = computed(
    () => customerCategory.value?.customerSponsorId
  );

  const customerSponsorInfo = computed(
    () => customerCategory.value?.customerSponsorInfo
  );

  const customerWebLink = computed(
    () => customerCategory.value?.customerWebLink
  );

  const customerColor = computed(() => customerCategory.value?.customerColor);

  const customerMedia = computed(() => customerCategory.value?.customerMedia);

  const customerBaseline = computed(
    () => customerCategory.value?.customerBaseline
  );

  const load = async () => {
    if (customerSponsorInfo.value) {
      const sponsorInfo = await sponsorInfosService.get({
        id: customerSponsorInfo.value,
      });

      if (sponsorInfo)
        customerSponsorInfoLink.value = `/orientation/${sponsorInfo.slug}`;
    }
  };

  return {
    customerCategory,
    hasCustomerBanner,
    hasCustomerHeader,
    customerSponsorId,
    customerWebLink,
    customerColor,
    customerMedia,
    customerBaseline,
    customerSponsorInfo,
    customerSponsorInfoLink,
    load,
  };
};
